import { z } from 'zod';
import { UuidSchema, IsoDateSchema, LocalTimeSchema } from '../common/ids.js';
import { SeriesStatusSchema } from '../common/enums.js';
import { PaginationQuerySchema } from '../common/pagination.js';
import { DiscoveryQuerySchema } from '../common/discovery.js';
/**
 * RFC 5545 recurrence rule. Basic sanity only — "starts with FREQ=" — per
 * the task brief; full grammar validation belongs to the `rrule` library on
 * the API side, not to this contract (a schema that re-implements RFC 5545
 * would drift from the library's own parser).
 */
export const RruleSchema = z.string().regex(/^FREQ=/, 'Must start with FREQ=');
export const ShiftSeriesAssigneeSchema = z.object({
    slotIndex: z.int().min(0),
    userId: UuidSchema.nullable(),
});
export const ShiftSeriesSchema = z.object({
    id: UuidSchema,
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.nullable(),
    name: z.string().min(1),
    rrule: RruleSchema,
    startTime: LocalTimeSchema,
    endTime: LocalTimeSchema,
    /** Derived server-side: `end_time <= start_time` (02-DATA-MODEL.md §5). */
    crossesMidnight: z.boolean(),
    requiredHeadcount: z.int().min(1),
    startsOn: IsoDateSchema,
    endsOn: IsoDateSchema.nullable(),
    materializedUntil: IsoDateSchema.nullable(),
    status: SeriesStatusSchema,
    createdBy: UuidSchema,
    assignees: z.array(ShiftSeriesAssigneeSchema),
});
export const ShiftSeriesCreateRequestSchema = z.object({
    contractId: UuidSchema,
    siteId: UuidSchema,
    zoneId: UuidSchema.optional(),
    name: z.string().min(1),
    rrule: RruleSchema,
    startTime: LocalTimeSchema,
    endTime: LocalTimeSchema,
    requiredHeadcount: z.int().min(1),
    startsOn: IsoDateSchema,
    endsOn: IsoDateSchema.optional(),
    assignees: z.array(ShiftSeriesAssigneeSchema),
});
/**
 * `PATCH /shift-series/:id` body. `applyTo` is NOT here — 03-API-CONTRACT.md
 * §5 shows it as a query string (`?applyTo=future_only`), so it gets its own
 * schema, `ShiftSeriesApplyToQuerySchema`.
 */
export const ShiftSeriesPatchRequestSchema = z
    .object({
    zoneId: UuidSchema.nullable(),
    name: z.string().min(1),
    rrule: RruleSchema,
    startTime: LocalTimeSchema,
    endTime: LocalTimeSchema,
    requiredHeadcount: z.int().min(1),
    endsOn: IsoDateSchema.nullable(),
    assignees: z.array(ShiftSeriesAssigneeSchema),
})
    .partial()
    .strict();
export const ShiftSeriesApplyToQuerySchema = z.object({
    applyTo: z.enum(['future_only', 'all']).default('future_only'),
});
/** `POST /shift-series/:id/generate` — manual materialization. */
export const ShiftSeriesGenerateRequestSchema = z.object({
    until: IsoDateSchema,
});
/**
 * Why a generate run produced nothing **on purpose** (D-07 rule 4).
 *
 * These are not `ErrorCode`s and must not be: the caller asked "materialize
 * this", the server did look, and the honest answer is "there was nothing to
 * do, and here is why" — a `200` with a reason, not a refused request. An
 * inactive contract, a paused or ended series, or a window that already ends
 * before it starts are all states an admin can see and fix; none of them is
 * a failure of the call.
 */
export const MaterializationRefusalSchema = z.enum([
    'CONTRACT_INACTIVE',
    'SERIES_NOT_ACTIVE',
    'EMPTY_WINDOW',
]);
/**
 * `POST /shift-series/:id/generate` response (Phase 1 change batch,
 * 2026-08-27, Track B; `refusal` narrowed in the Phase 2 batch to the enum
 * the materializer actually emits). `null` on a clean run.
 */
export const ShiftSeriesGenerateResponseSchema = z.object({
    seriesId: UuidSchema,
    created: z.int().min(0),
    skippedExisting: z.int().min(0),
    deleted: z.int().min(0),
    preserved: z.int().min(0),
    from: IsoDateSchema,
    to: IsoDateSchema,
    materializedUntil: IsoDateSchema.nullable(),
    refusal: MaterializationRefusalSchema.nullable(),
});
export const ShiftSeriesPauseRequestSchema = z.object({}).strict();
export const ShiftSeriesEndRequestSchema = z.object({}).strict();
export const ShiftSeriesListQuerySchema = DiscoveryQuerySchema.extend(PaginationQuerySchema.shape);
//# sourceMappingURL=series.js.map